import { Fragment } from 'react';
import { SuitSymbol } from '../common/SuitSymbol';

/**
 * A category or sub-category name as the data carries it — "After a 1♦ opening", "Overcalling
 * their preemptive 2♥/2♠" — with every suit glyph drawn through the shared `SuitSymbol`, so the
 * status line keeps the constitution's Suit Symbol Display standard (Arial/Arimo, red ♦♥).
 *
 * The names arrive from the prediction program already holding the glyphs rather than bid
 * tokens like "1D", which is why `BidText` is not the right tool here: it tokenises "1D"-style
 * text, and would leave a bare ♦ character in the page font.
 *
 * `<option>` text cannot carry markup, so the dropdowns show the raw name; this is for the
 * places that render HTML.
 */

const GLYPH_TO_SUIT: Record<string, 'C' | 'D' | 'H' | 'S'> = {
  '♣': 'C',
  '♦': 'D',
  '♥': 'H',
  '♠': 'S',
};

const GLYPH_PATTERN = /[♣♦♥♠]/g;

export interface CategoryNameProps {
  name: string;
  className?: string;
}

export function CategoryName({ name, className = '' }: CategoryNameProps) {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  GLYPH_PATTERN.lastIndex = 0;
  while ((match = GLYPH_PATTERN.exec(name)) !== null) {
    const suit = GLYPH_TO_SUIT[match[0]];
    if (!suit) continue; // unreachable: the pattern only matches the four glyphs
    if (match.index > lastIndex) parts.push(name.slice(lastIndex, match.index));
    parts.push(
      <SuitSymbol key={`${match.index}-${match[0]}`} suit={suit} className="suit-glyph-inherit" />
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < name.length) parts.push(name.slice(lastIndex));

  return (
    <span className={className}>
      {parts.map((p, i) => (
        <Fragment key={i}>{p}</Fragment>
      ))}
    </span>
  );
}
